/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import CampaignModel from 'cio/models/campaign';
import { task } from 'ember-concurrency';
import { inject as service } from '@ember/service';
import MetricsService from 'cio/services/metrics';
import { tracked } from '@glimmer/tracking';
import ActionModel, { VariationBadge } from 'cio/models/action';
import { SplitMetric } from 'cio/utils/metrics/models/split-metric';
import { MIN_TRIALS } from 'cio/utils/split-tests';
import { calculateCTBOForMeasure } from 'cio/utils/metrics/tests';
import { findDenominator, MeasureType } from 'cio/utils/metrics/measure';

interface CampaignOverviewPageActiveTestsCardArgs {
  campaign: CampaignModel;
}

export type ActiveTestsCardTest = {
  action: ActionModel;
  variations: {
    action: ActionModel;
    badge: VariationBadge;
    metric: SplitMetric | null;
    isOriginal: boolean;
  }[];
  hasEnoughDataForCTBO: boolean;
  CTBO?: {
    opened?: number | null;
    clicked?: number | null;
    converted?: number | null;
  };
};

export default class CampaignOverviewPageActiveTestsCard extends Component<CampaignOverviewPageActiveTestsCardArgs> {
  @service metrics!: MetricsService;

  @tracked tests: ActiveTestsCardTest[] = [];

  get activeTests() {
    return this.args.campaign.splitTestActions.filter((a) => a.isSplitRunning);
  }

  get denominator() {
    return findDenominator('converted', MeasureType.DeliveryMetrics, 'email_action');
  }

  fetchMetrics = task({ restartable: true }, async () => {
    let { activeTests } = this;

    if (activeTests.length === 0) {
      return;
    }

    let campaignSplitMetrics = await this.metrics.fetchCampaignSplitMetrics(this.args.campaign.id);

    this.tests = activeTests
      .map((a) => {
        let [originalMetric, variationMetric] = campaignSplitMetrics[a.id];
        let hasEnoughDataForCTBO =
          this.denominator != null ? (variationMetric[this.denominator].rawTotal ?? 0) >= MIN_TRIALS : false;

        let test: ActiveTestsCardTest = {
          action: a,
          variations: a.splitActions.map((v, j) => ({
            action: v.action,
            badge: v.badge,
            metric: campaignSplitMetrics[a.id]?.[j],
            isOriginal: j === 0,
          })),
          hasEnoughDataForCTBO,
        };

        if (hasEnoughDataForCTBO) {
          test.CTBO = {
            opened: calculateCTBOForMeasure(originalMetric, variationMetric, 'opened', 'email_action'),
            clicked: calculateCTBOForMeasure(originalMetric, variationMetric, 'clicked', 'email_action'),
            converted: calculateCTBOForMeasure(originalMetric, variationMetric, 'converted', 'email_action'),
          };
        }

        return test;
      })
      .sort((a, b) => {
        let nameA = a.action.splitTestName?.toLowerCase() ?? '';
        let nameB = b.action.splitTestName?.toLowerCase() ?? '';

        return nameA.localeCompare(nameB);
      });
  });
}
